import { Header, Paragraph } from "components/atoms/Typography";
import DataCoverage from "components/molecules/DataCoverage";
import { EnergyTypeInferredIcon } from "components/molecules/EnergyTypeInferredIcon/EnergyTypeInferredIcon";
import { Tag } from "pages/BuildingPage/BuildingTags";
import posthog from "posthog-js";
import { BUILDING_COLOURS, CURRENT_YEAR, PER_YEAR, buildingTypeOptions, energyTypeOptions } from "./constants";

const FORMATTING_ERROR_VALUE = "n/a";

const returnFormattingError = (value: any, error: any, functionName: string) => {
    posthog.capture("Error", {
        error: error.toString(),
    });

    console.log(error);
    console.log("when formatting value in %s: %s", functionName, value);
    return FORMATTING_ERROR_VALUE;
};

const buildTooltipNode = (introText: string, howToReadText: string) => (
    <div style={{ display: "grid", gap: "24px" }}>
        <Paragraph size="small">{introText}</Paragraph>
        <div style={{ display: "grid", gap: "12px" }}>
            <Header size="x-small" style={{ color: "var(--audette-gray-500)" }}>
                How to read
            </Header>
            <Paragraph size="small">{howToReadText}</Paragraph>
        </div>
    </div>
);

export const calculateRoundedPercentageFromRatio = (ratio: number) => Math.round(ratio * 100);

export const formatAbatementCost = (value: number) => formatNumberWithCommas(value, 0);

export const getCarbonEmissionIntensityColour = (carbonEmissionIntensity: number) => {
    if (carbonEmissionIntensity >= 2.4868412767468726) return BUILDING_COLOURS.high;
    if (carbonEmissionIntensity >= 1.7732741126584752) return BUILDING_COLOURS.mid;
    return BUILDING_COLOURS.low;
};

export const getDebtToValueRatioColour = (debtToValueRatio: number) => {
    if (debtToValueRatio > 0.05) return BUILDING_COLOURS.high;
    if (debtToValueRatio > 0.02) return BUILDING_COLOURS.mid;
    return BUILDING_COLOURS.low;
};

export const formatRatio = (value: number) => {
    try {
        return value.toFixed(2).toLowerCase();
    } catch (error) {
        return returnFormattingError(value, error, "formatRatio");
    }
};

export const formatRatioAsPercentage = (value: number, decimalPlaces: number = 2) => {
    try {
        return parseFloat((value * 100).toFixed(decimalPlaces)).toLocaleString();
    } catch (error) {
        return returnFormattingError(value, error, "formatRatioAsPercentage");
    }
};

const formatTags = (value: string[] | string) => {
    try {
        if (Array.isArray(value)) {
            const numberOfTags = value.length;
            const maxTagsToShow = 4;
            return (
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px",
                    }}
                >
                    {value.slice(0, maxTagsToShow).map((tag) => (
                        <Tag key={tag} value={tag} />
                    ))}
                    {numberOfTags > maxTagsToShow && (
                        <Tag key={numberOfTags} value={`+${numberOfTags - maxTagsToShow}`} />
                    )}
                </div>
            );
        }
        return <Tag key={value} value={value} />;
    } catch (error) {
        return returnFormattingError(value, error, "formatTags");
    }
};

export const formatPercentage = (value: number) => {
    try {
        const formattedValue = value.toFixed(0).toLocaleString();
        if (formattedValue === "-0") return "0";
        return formattedValue;
    } catch (error) {
        return returnFormattingError(value, error, "formatPercentage");
    }
};

export const formatNumberWithCommas = (value: number, decimalPlaces: number) => {
    try {
        const formattedValue = parseFloat(value.toFixed(decimalPlaces)).toLocaleString(undefined, {
            minimumFractionDigits: decimalPlaces,
        });
        if (formattedValue === "-0") return "0";
        return formattedValue;
    } catch (error) {
        return returnFormattingError(value, error, "formatNumberWithCommas");
    }
};

export const assembleBuildingAddress = (location: Location) => {
    try {
        const addressComponents = [
            location.streetAddress,
            location.city,
            location.stateProvince,
            location.postalZipCode,
            location.country,
        ].filter((i) => i !== null);
        return addressComponents.join(", ");
    } catch (error) {
        return returnFormattingError(location, error, "assembleBuildingAddress");
    }
};

const formatUtilityRate = (value: number | null) => {
    try {
        if (value === null) return "";
        return value.toFixed(3).toLocaleString();
    } catch (error) {
        return returnFormattingError(value, error, "formatUtilityRate");
    }
};

const formatLocationAttribute = (attribute: string | null) => {
    try {
        return attribute || "-";
    } catch (error) {
        return returnFormattingError(attribute, error, "formatLocationAttribute");
    }
};

const getMonetaryUnit = (value: number, key?: string) => {
    const thousand = 1000;
    const million = 1000000;
    const billion = 1000000000;

    const absValue = Math.abs(value);
    if (key === "totalMeasureCostIntensity" || key === "netPresentValueIntensity") {
        return "$";
    }

    if (key === "cumulativeCarbonFines" || key === "carbonFinesUnsaveable" || key === "cumulativeCarbonFineSavings") {
        if (absValue >= billion) return "billion(s) of dollars";
        if (absValue >= million) return "million(s) of dollars";
        if (absValue >= thousand) return "thousand(s) of dollars";

        return "$";
    }

    if (absValue >= billion) return "B\xa0$";
    if (absValue >= million) return "k\xa0$"; // swapped M for k for standardization
    if (absValue >= thousand) return "k\xa0$";

    return "k\xa0$";
};

const formatCostFromUnit = (unit: string, showCents: boolean) => {
    const thousand = 1000;
    const million = 1000000;
    const billion = 1000000000;

    const newUnit = unit.replace(PER_YEAR, "");

    return (value: number, showDecimals: boolean = true) => {
        try {
            let numberOfDecimals = showDecimals ? 2 : 0;

            if (newUnit === "billion(s) of dollars") {
                const scaledValue = value / billion;
                numberOfDecimals = scaledValue >= 1 ? 1 : 2;
                return formatNumberWithCommas(scaledValue, numberOfDecimals);
            }
            if (newUnit === "million(s) of dollars") {
                const scaledValue = value / million;
                if (scaledValue >= 1) {
                    numberOfDecimals = 1;
                } else if (scaledValue >= 0.1) {
                    numberOfDecimals = 2;
                } else {
                    numberOfDecimals = 3;
                }
                return formatNumberWithCommas(scaledValue, numberOfDecimals);
            }
            if (newUnit === "thousand(s) of dollars") {
                const scaledValue = value / thousand;
                numberOfDecimals = scaledValue >= 1000 ? 0 : 1;
                return formatNumberWithCommas(scaledValue, numberOfDecimals);
            }

            if (newUnit === "B\xa0$") return formatNumberWithCommas(value / billion, numberOfDecimals);
            if (newUnit === "M\xa0$") return formatNumberWithCommas(value / million, numberOfDecimals);
            if (newUnit === "k\xa0$") {
                if (value >= 1000) numberOfDecimals = 0;
                if (value < 0.1 && value >= 0.0) numberOfDecimals = 1;
                return formatNumberWithCommas(value / thousand, numberOfDecimals);
            }

            return formatNumberWithCommas(value, showCents ? 2 : 0);
        } catch (error) {
            return returnFormattingError(value, error, "formatCostFromUnit");
        }
    };
};

const formatCostIntensityFromUnit = (unit: string) => formatCostFromUnit(unit, true);

const formatMonetaryValueFromUnit = (unit: string) => formatCostFromUnit(unit, false);

const getGrossFloorAreaUnit = (value: number) => {
    const million = 1000000;
    const billion = 1000000000;

    const absValue = Math.abs(value);

    if (absValue >= billion) return "Bft²";
    if (absValue >= million) return "Mft²";

    return "ft²";
};

const formatGrossFloorAreaFromUnit = (unit: string) => {
    const million = 1000000;
    const billion = 1000000000;

    return (value: number, showDecimals: boolean = true) => {
        try {
            let numberOfDecimals = showDecimals ? 2 : 0;

            if (unit.includes("B")) return formatNumberWithCommas(value / billion, numberOfDecimals);
            if (unit.includes("M")) return formatNumberWithCommas(value / million, numberOfDecimals);

            numberOfDecimals = 0; // No decimals for ft²
            return formatNumberWithCommas(value, numberOfDecimals);
        } catch (error) {
            return returnFormattingError(value, error, "formatGrossFloorAreaFromUnit");
        }
    };
};

export const formatMonetaryValue = (value: number, decimals = 2) => formatNumberWithCommas(value, decimals);

export const formatEmissions = (emissions: number) => formatNumberWithCommas(parseFloat(emissions.toPrecision(3)), 1);

export const formatIntensity = (intensity: number) => formatNumberWithCommas(intensity, 4);

const formatCostIntensity = (intensity: number) => formatNumberWithCommas(intensity, 2);

export const formatEnergy = (energy: number) => formatNumberWithCommas(energy, 0);

const formatEnergyFromUnit = (unit: string) => {
    const thousand = 1000;
    const million = 1000000;
    const billion = 1000000000;
    const trillion = 1000000000000;

    const newUnit = unit.replace(PER_YEAR, "");

    return (value: number, showDecimals = true) => {
        try {
            const numberOfDecimals = showDecimals ? 2 : 0;

            if (newUnit === "kWh") return formatNumberWithCommas(value, numberOfDecimals);
            if (newUnit === "MWh") return formatNumberWithCommas((value * thousand) / million, numberOfDecimals);
            if (newUnit === "GWh") return formatNumberWithCommas((value * thousand) / billion, numberOfDecimals);
            if (newUnit === "TWh") return formatNumberWithCommas((value * thousand) / trillion, numberOfDecimals);
            return formatNumberWithCommas(value, numberOfDecimals);
        } catch (error) {
            return returnFormattingError(value, error, "formatEnergyFromUnit");
        }
    };
};

const getEnergyUnit = (value: number) => {
    const absValue = Math.abs(value);

    const million = 1000000;
    const billion = 1000000000;
    const trillion = 1000000000000;
    const thousand = 1000;

    if (absValue * thousand >= trillion) return "TWh"; // divide by thousand because raw value is always kWh
    if (absValue * thousand >= billion) return "GWh";
    if (absValue * thousand >= million) return "MWh";
    return "kWh";
};

const getEmissionsUnit = (value: number) => {
    const kTonnes = 1000;
    const MTonnes = 1000000;
    const gTonnes = 1000000000;

    const absValue = Math.abs(value);

    if (absValue >= gTonnes) return "GtCO₂e";
    if (absValue >= MTonnes) return "MtCO₂e";
    if (absValue >= kTonnes) return "tCO₂e"; // Initially ktCo2e, changed to tCO₂e for standardization accross app
    return "tCO₂e";
};

const formatEmissionsFromUnit = (unit: string) => {
    const kTonnes = 1000;
    const MTonnes = 1000000;
    const gTonnes = 1000000000;

    const newUnit = unit.replace(PER_YEAR, "");

    return (value: number, showDecimals: boolean = true) => {
        const numberOfDecimals = showDecimals ? 2 : 0;

        if (newUnit === "tCO₂e") return formatNumberWithCommas(value, 0); //  UX requirement: don't show decimals for this unit
        if (newUnit === "ktCO₂e") return formatNumberWithCommas(value / kTonnes, numberOfDecimals);
        if (newUnit === "MtCO₂e") return formatNumberWithCommas(value / MTonnes, numberOfDecimals);
        if (newUnit === "GtCO₂e") return formatNumberWithCommas(value / gTonnes, numberOfDecimals);
        return formatNumberWithCommas(value, numberOfDecimals);
    };
};

export const formatGrossFloorArea = (grossFloorArea: number) =>
    formatNumberWithCommas(parseFloat(grossFloorArea.toPrecision(2)), 0);

export const formatReturnRatio = (returnRatio: number) => formatRatioAsPercentage(returnRatio, 0);

export const formatBuildingArchetype = (buildingArchetype: string) => {
    try {
        return buildingTypeOptions.filter((x) => x.key === buildingArchetype)[0]?.label || "Unknown";
    } catch (error) {
        return returnFormattingError(buildingArchetype, error, "formatBuildingArchetype");
    }
};

export const formatImplementationStatus = (status: string | null) => {
    if (status === null || status === "") {
        return "No status";
    }
    return status[0].toUpperCase() + status.slice(1);
};

export const formatEnergyType = (energyType: EnergyTypeEnum) => {
    try {
        const label = energyTypeOptions.filter((x) => x.key === energyType)[0]?.label || "Unknown";

        if (energyType.toString().includes("inferred")) return <EnergyTypeInferredIcon label={label} />;

        return label;
    } catch (error) {
        return returnFormattingError(energyType, error, "formatEnergyType");
    }
};

export const formatMeasureCoverage = (measureCoverage: number, numberOfBuildings: number) => {
    try {
        return `${measureCoverage} (${((measureCoverage / numberOfBuildings) * 100).toFixed()}%)`;
    } catch (error) {
        return returnFormattingError(numberOfBuildings, error, "formatMeasureCoverage");
    }
};

const formatUnit = (unit: string) => {
    if (unit === "kwh") return "kWh";
    if (unit === "gj") return "GJ";
    if (unit === "m3") return "m³";
    if (unit === "ft3") return "ft³";
    return unit;
};

export const formatSimplePayback = (simplePayback: number) => {
    try {
        const payback = Math.ceil(simplePayback);
        if (payback === Infinity) return "None";
        if (payback > 50) return "50+";
        return payback;
    } catch (error) {
        return returnFormattingError(simplePayback, error, "formatSimplePayback");
    }
};

export const formatSimplePaybackNotCapped = (simplePayback: number) => {
    try {
        const payback = Math.ceil(simplePayback);
        if (payback < 0) return "None";
        return payback;
    } catch (error) {
        return returnFormattingError(simplePayback, error, "formatSimplePayback");
    }
};

const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

// accepts date in format yyyy-mm-dd
const formatDate = (date: string, long: boolean = true) => {
    try {
        const splitDate = date.split("-");
        const year = splitDate[0];
        const month = Number(splitDate[1]);
        const day = splitDate[2];

        if (long) return `${day} ${months[month - 1]}, ${year}`;
        return `${day}/${month}/${year}`;
    } catch (error) {
        return returnFormattingError(date, error, "formatDate");
    }
};

export const formatIsoToDateString = (iso: string) => {
    try {
        const newDate = new Date(iso);
        return newDate.toLocaleDateString("en-uk", {
            day: "numeric",
            month: "numeric",
            year: "numeric",
        });
    } catch (error) {
        return returnFormattingError(iso, error, "formatIsoToDateString");
    }
};

const formatIsoToDatetimeString = (iso: string) => {
    try {
        const newDate = new Date(iso);
        return newDate.toLocaleDateString("en-uk", {
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: true,
        });
    } catch (error) {
        return returnFormattingError(iso, error, "formatIsoToDatetimeString");
    }
};

const formatLatitudeLongitude = (v: number) => formatNumberWithCommas(v, 5);

export const formatYearApplied = (yearApplied: number) => {
    try {
        return Math.ceil(yearApplied);
    } catch (error) {
        return returnFormattingError(yearApplied, error, "formatYearApplied");
    }
};

export const formatDebtToValueRatio = (debtToValueRatio: number) => {
    try {
        return (debtToValueRatio * 100).toFixed();
    } catch (error) {
        return returnFormattingError(debtToValueRatio, error, "formatDebtToValueRatio");
    }
};

const formatBuildingAttribute = (attribute: number) => {
    try {
        return parseFloat(attribute.toFixed(2)).toLocaleString();
    } catch (error) {
        return returnFormattingError(attribute, error, "formatBuildingAttribute");
    }
};

const formatUtilityType = (attribute: string) =>
    attribute
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

export const formatRatioForDisplay = (value: number | string, propertyKey: keyof any) => {
    try {
        if (typeof value === "string") return value;
        if (ratioKeys.includes(propertyKey)) return formatRatioAsPercentage(value);
        return value;
    } catch (error) {
        return returnFormattingError(value, error, "formatRatioForDisplay");
    }
};

export const formatRatioForAPI = (value: number, buildingPropertyKey: keyof BuildingProperties) => {
    try {
        if (ratioKeys.includes(buildingPropertyKey)) return formatPercentageAsRatio(value);
        return value;
    } catch (error) {
        return returnFormattingError(value, error, "formatRatioForAPI");
    }
};

const formatBuildingName = (name: string) => {
    try {
        if (name.length < 1) return name;
        return name[0].toUpperCase() + name.slice(1);
    } catch (error) {
        return returnFormattingError(name, error, "formatBuildingName");
    }
};

/*
 * [2025-10-15 TRC] This is not great.  This should somenow be attached directly to the
 * entry in the massive semanticTermToDisplayInfo where all other info about various
 * fields is stored
 */
const ratioKeys: (keyof any)[] = [
    "availableRoofAreaRatio",
    "ledInstalledRatio",
    "defaultDiscountRate",
    "defaultWeightEmissionSavings",
    "defaultWeightNetPresentValue",
    "defaultUtilityInflationRate",
    "defaultLandlordShareCost",
    "defaultLandlordShareElectricity",
    "defaultLandlordShareNaturalGas",
    "defaultLandlordShareSteam",
    "assumedExitCapRate",
];

const formatPercentageAsRatio = (value: number) => {
    try {
        return parseFloat((Number(value) * 0.01).toPrecision(12));
    } catch (error) {
        return returnFormattingError(value, error, "formatPercentageAsRatio");
    }
};

const validateYear = (year: number) => year >= 1900 && year <= CURRENT_YEAR;
const validatePercentage = (perc: number) => perc >= 0 && perc <= 100;
const validatePositiveNumber = (n: number) => n >= 0;
const validateExitYear = (year: number) => year >= CURRENT_YEAR && year <= 2100;

export const semanticTermToDisplayInfo: {
    [key: string]: DisplayInfo;
} = {
    accountNumber: {
        humanReadable: "Account number",
    },
    landlordShareCost: {
        humanReadable: "Landlord share cost",
        unit: "%",
        formatFunction: formatRatioAsPercentage,
    },
    landlordShareElectricity: {
        humanReadable: "Landlord share electricity",
        unit: "%",
        formatFunction: formatRatioAsPercentage,
    },
    landlordShareNaturalGas: {
        humanReadable: "Landlord share electricity",
        unit: "%",
        formatFunction: formatRatioAsPercentage,
    },
    landlordShareSteam: {
        humanReadable: "Landlord share steam",
        unit: "%",
        formatFunction: formatRatioAsPercentage,
    },
    netLandlordCashflow: {
        humanReadable: "Net landlord cashflow",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The landlord share of the new measure cost minus the landlord share of the net savings",
    },
    netLandlordIncrementalCost: {
        humanReadable: "Net landlord incremental cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The landlord share of the incemental measure cost, minus any incentives",
    },
    splitNetIncrementalCost: {
        humanReadable: "Net incremental cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The split share of the incemental measure cost, minus any incentives",
    },
    netLandlordTotalCost: {
        humanReadable: "Net landlord total cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The landlord share of the total measure cost, minus any incentives",
    },
    splitNetTotalCost: {
        humanReadable: "Net total cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The split share of the total measure cost, minus any incentives",
    },
    netLandlordCost: {
        humanReadable: "Net landlord cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The landlord share of the measure cost, minus any incentives",
    },
    splitNetCost: {
        humanReadable: "Net cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The split share of the measure cost, minus any incentives",
    },
    netLandlordSavings: {
        humanReadable: "Net landlord savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The landlord share of savings from utility costs, carbon costs and revenue.",
    },
    splitNetSavings: {
        humanReadable: "Net savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The split share of savings from utility costs, carbon costs and revenue.",
    },
    netSavings: {
        humanReadable: "Total savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The landlord share of savings from utility costs, carbon costs and revenue.",
    },
    netCost: {
        humanReadable: "Total cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The savings from utility costs, carbon costs and revenue.",
    },
    implementationCost: {
        humanReadable: "Implementation Cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip:
            "The cost to implement the measure.  Might be total cost or just incremental cost, depending on the context.",
    },
    annualCarbonEmissionIntensityCurrent: {
        humanReadable: "Emissions intensity",
        unit: "kg\xa0CO₂e/ft²/yr",
        formatFunction: formatIntensity,
        placeholder: "Enter amount",
        tooltip: "The annual emissions of a building in the first year of the plan normalized by gross floor area.",
    },
    annualCarbonEmissionsCurrent: {
        humanReadable: "Annual carbon emissions",
        unit: "tCO₂e",
        formatFunction: formatEmissions,
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
        placeholder: "Enter amount",
    },
    annualCarbonEmissionSavingsElectricity: {
        humanReadable: "Emission savings electricity",
        unit: `tCO₂e${PER_YEAR}`,
        formatFunction: formatEmissions,
        getUnit: (value: number) => `${getEmissionsUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatEmissionsFromUnit,
        tooltip:
            "The average electricity emission savings expected after project implementation, accounting for future grid projections.",
    },
    annualCarbonEmissionSavingsNaturalGas: {
        humanReadable: "Emission savings natural gas",
        unit: `tCO₂e${PER_YEAR}`,
        formatFunction: formatEmissions,
        getUnit: (value: number) => `${getEmissionsUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatEmissionsFromUnit,
        tooltip:
            "The average natural gas emission savings expected after project implementation, accounting for future grid projections.",
    },
    annualCarbonEmissionSavingsSteam: {
        humanReadable: "Emission savings steam",
        unit: `tCO₂e${PER_YEAR}`,
        formatFunction: formatEmissions,
        getUnit: (value: number) => `${getEmissionsUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatEmissionsFromUnit,
        tooltip:
            "The average steam emission savings expected after project implementation, accounting for future grid projections.",
    },
    annualCarbonEmissionSavingsTotal: {
        humanReadable: "Average annual emission savings",
        unit: `tCO₂e${PER_YEAR}`,
        formatFunction: formatEmissions,
        getUnit: (value: number) => `${getEmissionsUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatEmissionsFromUnit,
        tooltip:
            "The average total emission savings expected after project implementation, accounting for future grid projections.",
    },
    annualCarbonEmissionSavings: {
        humanReadable: "Average annual emission savings",
        unit: `tCO₂e${PER_YEAR}`,
        formatFunction: formatEmissions,
        getUnit: (value: number) => `${getEmissionsUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatEmissionsFromUnit,
        tooltip:
            "The average total emission savings expected after project implementation, accounting for future grid projections.",
    },
    annualCarbonEmissionSavings2050: {
        humanReadable: "Annual carbon emission savings in 2050",
        unit: "tCO₂e",
        formatFunction: formatEmissions,
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
    },
    annualCarbonPolicyCostIntensityCurrent: {
        humanReadable: "Baseline carbon policy cost intensity",
        unit: "$/ft²",
        formatFunction: formatCostIntensity,
        tooltip: "The total expected carbon policy cost over all plan years, normalized by floor area.",
    },
    annualCarbonTaxIntensityCurrent: {
        humanReadable: "Carbon tax  intensity",
        unit: "$/ft²/yr",
        formatFunction: formatCostIntensity,
        tooltip: "The average annual carbon tax normalized by floor area.",
    },
    annualCarbonPolicyCostSavings: {
        humanReadable: "Carbon cost savings",
        unit: `$${PER_YEAR}`,
        formatFunction: formatMonetaryValue,
        getUnit: (value: number) => `${getMonetaryUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatMonetaryValueFromUnit,
        tooltip: "The average annual carbon cost savings expected after project implementation.",
    },
    annualCarbonTaxSavings: {
        humanReadable: "Carbon tax savings",
        unit: `$${PER_YEAR}`,
        formatFunction: formatMonetaryValue,
        getUnit: (value: number) => `${getMonetaryUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatMonetaryValueFromUnit,
        tooltip: "The average annual carbon tax savings expected after project implementation.",
    },
    annualEnergyCostSavings: {
        humanReadable: "Utility savings",
        unit: `$${PER_YEAR}`,
        getUnit: (value: number) => `${getMonetaryUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The average annual utility savings expected after project implementation.",
    },
    annualEnergyConsumptionCurrent: {
        humanReadable: "Annual energy consumption",
        unit: "kWh",
        getUnit: getEnergyUnit,
        formatFromUnit: formatEnergyFromUnit,
        formatFunction: formatEnergy,
        placeholder: "Enter amount",
    },
    annualEnergyConsumptionSavingsElectricity: {
        humanReadable: "Annual energy savings electricity",
        unit: `kWh${PER_YEAR}`,
        getUnit: (value: number) => `${getEnergyUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatEnergyFromUnit,
        formatFunction: formatEnergy,
        tooltip: "The annual energy savings from electricity expected from project implementation.",
    },
    annualEnergyConsumptionSavingsNaturalGas: {
        humanReadable: "Annual energy savings natural gas",
        unit: `kWh${PER_YEAR}`,
        getUnit: (value: number) => `${getEnergyUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatEnergyFromUnit,
        formatFunction: formatEnergy,
        tooltip: "The annual energy savings from natural gas expected from project implementation.",
    },
    annualEnergyConsumptionSavingsSteam: {
        humanReadable: "Annual energy savings steam",
        unit: `kWh${PER_YEAR}`,
        getUnit: (value: number) => `${getEnergyUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatEnergyFromUnit,
        formatFunction: formatEnergy,
        tooltip: "The annual energy savings from steam expected from project implementation.",
    },
    annualEnergyConsumptionSavingsTotal: {
        humanReadable: "Total annual energy savings",
        unit: `kWh${PER_YEAR}`,
        getUnit: (value: number) => `${getEnergyUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatEnergyFromUnit,
        formatFunction: formatEnergy,
        tooltip: "The annual total energy savings expected from project implementation.",
    },
    annualEnergyConsumptionSavings: {
        humanReadable: "Average annual energy savings",
        unit: `kWh${PER_YEAR}`,
        getUnit: (value: number) => `${getEnergyUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatEnergyFromUnit,
        formatFunction: formatEnergy,
        tooltip: "The annual total energy savings expected after project implementation.",
    },
    annualEnergyUseIntensityCurrent: {
        humanReadable: "Energy intensity",
        unit: "kWh/ft²/yr",
        formatFunction: formatIntensity,
        tooltip:
            "The annual energy consumption of a building in the first year of the plan normalized by gross floor area.",
    },
    annualUtilitySavings: {
        humanReadable: "Utility savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    annualUtilityCostIntensityCurrent: {
        humanReadable: "Utility cost intensity",
        unit: "$/ft²/yr",
        formatFunction: formatCostIntensity,
        tooltip: "The average annual utility cost normalized by floor area.",
    },
    annualMeanCarbonEmissionSavings: {
        humanReadable: "Annual carbon emissions savings",
        unit: "tCO₂e",
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
        formatFunction: formatEmissions,
    },
    annualUtilityCostSavingsElectricity: {
        humanReadable: "Utility cost savings electricity",
        unit: "$/yr",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    annualUtilityCostSavingsNaturalGas: {
        humanReadable: "Utility cost savings natural gas",
        unit: "$/yr",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    annualUtilityCostSavingsSteam: {
        humanReadable: "Utility cost savings steam",
        unit: "$/yr",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    annualMeanUtilityCostSavings: {
        humanReadable: "Mean utility cost savings",
        unit: "$/yr",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    availableRoofAreaRatio: {
        humanReadable: "Available area",
        unit: "%",
        description: "The percentage of the total roof area that is unobstructed (ie. free from equipment, parapets)",
        formatFunction: formatRatioAsPercentage,
        errorMessage: "Must be a valid percentage between 0 and 100",
        isValidFn: validatePercentage,
    },
    averageRate: {
        humanReadable: "Average rate",
        formatFunction: formatUtilityRate,
        unit: "$/kWh",
    },
    utilityRatesElectricity: {
        humanReadable: "Utility rate electricity",
        formatFunction: formatUtilityRate,
        unit: "$/kWh",
    },
    utilityRatesNaturalGas: {
        humanReadable: "Utility rate natural gas",
        formatFunction: formatUtilityRate,
        unit: "$/kWh",
    },
    averageMarginalAbatementCost: {
        humanReadable: "Average marginal abatement cost",
        unit: "$/tCO₂e saved",
        formatFunction: formatAbatementCost,
        tooltip: buildTooltipNode(
            "The negative NPV $ divided by the lifetime carbon emission savings of the proposed project.",
            "A negative value means the project offers a net benefit, where benefits exceed costs. Contrary, a positive value indicates a net cost, where project costs exceed benefits."
        ),
    },
    baseline: {
        humanReadable: "Baseline",
        unit: "tCO₂e",
    },
    buildingArchetype: {
        humanReadable: "Building type",
        formatFunction: formatBuildingArchetype,
        placeholder: "type",
    },
    buildingCount: {
        humanReadable: "Building count",
    },
    buildingHeight: {
        humanReadable: "Building height",
        unit: "ft",
        formatFunction: formatBuildingAttribute,
        placeholder: "Enter height",
        errorMessage: "Floor area should be a positive number",
        isValidFn: (area: number) => area > 0,
    },
    buildingsImpacted: {
        humanReadable: "Buildings Impacted",
    },
    buildingModelUid: {
        humanReadable: "Building model UID",
    },
    buildingName: {
        humanReadable: "Building name",
        formatFunction: formatBuildingName,
        placeholder: "Enter name",
    },
    calendarYear: {
        humanReadable: "Year",
    },
    annualCapex: {
        humanReadable: "CapEx",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalCapex: {
        humanReadable: "CapEx",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    carbonEmissions: {
        humanReadable: "Total annual carbon emissions",
        unit: "tCO₂e",
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
        formatFunction: formatEmissions,
    },
    carbonEmissionIntensity: {
        humanReadable: "Average emission intensity",
        unit: "kg\xa0CO₂e/ft²",
        formatFunction: formatIntensity,
        placeholder: "25",
    },
    carbonEmissionReductionPotential: {
        humanReadable: "Emissions reduction potential ",
        unit: "%",
        formatFunction: (v: number) => formatRatioAsPercentage(v, 0),
        tooltip:
            "Emissions reduction potential by 2050 compared to the baseline scenario, assuming all projects are implemented.",
    },
    carbonReductionMeasureCategory: {
        humanReadable: "Measure category",
    },
    carbonReductionMeasureLife: {
        humanReadable: "Lifetime",
        unit: "yrs",
    },
    carbonReductionMeasureSize: {
        humanReadable: "Sizing",
        formatFunction: (v: number) => v.toFixed(0),
    },
    carbonReductionMeasureType: {
        humanReadable: "Measure name",
    },
    carbonReductionMeasureUnitCost: {
        humanReadable: "Unit cost",
        unit: "$",
        formatFunction: (v: number) => v.toFixed(0),
    },
    carbonPolicyCost: {
        humanReadable: "Carbon policy cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    carbonPolicyCostIntensity: {
        humanReadable: "Carbon policy cost intensity",
        unit: "$/ft²",
        formatFunction: formatCostIntensity,
        tooltip: "The average annual carbon policy cost normalized by floor area.",
    },
    carbonPolicyCostSavings: {
        humanReadable: "Carbon policy cost savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    cashFlowAnnual: {
        humanReadable: "Net cash flow",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    cashFlowAnnualHidden: {
        humanReadable: "Net cash flow",
        formatFunction: () => "",
    },
    cashFlowCumulative: {
        humanReadable: "Cumulative net cash flow",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    createdAt: {
        humanReadable: "Date created",
        formatFunction: formatIsoToDateString,
    },
    currency: {
        humanReadable: "Currency",
        formatFunction: (currency: string) => currency.toUpperCase(),
    },
    city: {
        humanReadable: "City",
        formatFunction: formatLocationAttribute,
        placeholder: "Enter city",
    },
    climateZone: {
        humanReadable: "Climate Zone",
    },
    country: {
        humanReadable: "Country",
        placeholder: "Enter country",
    },
    dataCoverage: {
        humanReadable: "Data coverage",
        formatFunction: (dataCoverage: DataCoverage) => <DataCoverage dataCoverage={dataCoverage} />,
    },
    dataCoverages: {
        humanReadable: "Data coverage",
    },
    energyType: {
        humanReadable: "Energy type",
        formatFunction: formatEnergyType,
    },
    endDate: {
        humanReadable: "End date",
        formatFunction: formatDate,
    },
    debtToValueRatio: {
        humanReadable: "Carbon debt to asset value ratio",
        unit: "%",
        formatFunction: formatDebtToValueRatio,
    },
    decarbonizationPercentage: {
        humanReadable: "Decarbonization",
        unit: "%",
        formatFunction: formatPercentage,
    },
    discountRate: {
        humanReadable: "Discount rate",
        unit: "%",
        formatFunction: formatPercentage,
    },
    energyConsumption: {
        humanReadable: "Total annual energy consumption",
        unit: "kWh",
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
        placeholder: "19,700,000",
    },
    energyConsumptionUnit: {
        humanReadable: "Unit",
        formatFunction: formatUnit,
    },
    energyConsumptionSavings: {
        humanReadable: "Annual utility consumption savings in 2050",
        unit: "kWh",
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
    },
    energyUseIntensity: {
        humanReadable: "Average energy intensity",
        unit: "kWh/ft²",
        formatFunction: formatIntensity,
    },
    exposure: {
        humanReadable: "Cumulative carbon policy costs",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    fineUnits: {
        humanReadable: "Fine unit",
    },
    fineRate: {
        humanReadable: "Fine rate",
    },
    fineCurrencyCode: {
        humanReadable: "Fine currency code",
    },
    floorsAboveGrade: {
        humanReadable: "Floors above grade",
        formatFunction: formatBuildingAttribute,
        placeholder: "Enter floors",
        errorMessage: `Floors above grade should be a valid positive integer`,
        isValidFn: (floors: number) => floors > 0 && Number.isInteger(floors),
    },
    footprintArea: {
        humanReadable: "Footprint area",
        unit: "ft²",
        formatFunction: formatBuildingAttribute,
    },
    fundName: {
        humanReadable: "Fund name",
    },
    goal: {
        humanReadable: "Goal",
        unit: "tCO₂e",
    },
    grossFloorArea: {
        humanReadable: "Gross floor area",
        unit: "ft²",
        formatFunction: formatGrossFloorArea,
        getUnit: getGrossFloorAreaUnit,
        formatFromUnit: formatGrossFloorAreaFromUnit,
        placeholder: "Enter amount",
        errorMessage: "Floor area should be a positive number",
        isValidFn: (area: number) => area > 0,
    },
    grossFloorAreaMin: {
        humanReadable: "Minimum gross floor area",
        unit: "ft²",
        formatFunction: formatGrossFloorArea,
        getUnit: getGrossFloorAreaUnit,
        formatFromUnit: formatGrossFloorAreaFromUnit,
        placeholder: "Enter amount",
        errorMessage: "Floor area should be a positive number",
        isValidFn: (area: number) => area > 0,
    },
    grossFloorAreaMax: {
        humanReadable: "Maximum gross floor area",
        unit: "ft²",
        formatFunction: formatGrossFloorArea,
        getUnit: getGrossFloorAreaUnit,
        formatFromUnit: formatGrossFloorAreaFromUnit,
        placeholder: "Enter amount",
        errorMessage: "Floor area should be a positive number",
        isValidFn: (area: number) => area > 0,
    },
    impactPercentage: {
        humanReadable: "Impact",
        unit: "%",
        formatFunction: (value: number) => {
            if (Math.round(value) < 1) return "< 1";
            return formatPercentage(value);
        },
    },
    likeForLikeCost: {
        humanReadable: "Like for like cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "Baseline cost of a like-for-like replacement including installation costs.",
    },
    implementationStatus: {
        humanReadable: "Implementation status",
        formatFunction: formatImplementationStatus,
    },
    incrementalCost: {
        humanReadable: "Incremental cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip:
            "Incremental cost of the project compared to the baseline cost of the like-for-like replacement including installation costs.",
    },
    insertTime: {
        humanReadable: "Entry date",
        formatFunction: formatIsoToDateString,
    },
    internalRateOfReturn: {
        humanReadable: "IRR",
        unit: "%",
        formatFunction: (v: number) => formatRatioAsPercentage(v, 0),
        tooltip: buildTooltipNode(
            "The rate at which the present value of all future cash flows from an investment equals to its initial cost.",
            "If the IRR is greater than the cost of capital, the project is considered potentially profitable."
        ),
    },
    internalRateOfReturnIncremental: {
        humanReadable: "IRR incremental",
        unit: "%",
        formatFunction: (v: number) => formatRatioAsPercentage(v, 0),
        tooltip: buildTooltipNode(
            "The rate at which the present value of all future cash flows from an investment equals to its initial cost.",
            "If the IRR is greater than the cost of capital, the project is considered potentially profitable."
        ),
    },
    internalRateOfReturnTotal: {
        humanReadable: "IRR total",
        unit: "%",
        formatFunction: (v: number) => formatRatioAsPercentage(v, 0),
        tooltip: buildTooltipNode(
            "The rate at which the present value of all future cash flows from an investment equals to its initial cost.",
            "If the IRR is greater than the cost of capital, the project is considered potentially profitable."
        ),
    },
    energyConsumptionSavingsElectricity: {
        humanReadable: "Annual electricity consumption savings",
        unit: "kWh/yr",
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
        tooltip: "The annual consumption of electricity saved over the lifetime of the measure.",
    },
    energyConsumptionSavingsNaturalGas: {
        humanReadable: "Annual natural gas consumption savings",
        unit: `kWh${PER_YEAR}`,
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
        tooltip: "The total consumption of natural gas saved over the lifetime of the measure.",
    },
    lifetimeSteamConsumptionSavings: {
        humanReadable: "Lifetime steam consumption savings",
        unit: "kWh",
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
        tooltip: "The total consumption of steam saved over the lifetime of the measure.",
    },
    lastUpdated: {
        humanReadable: "Last updated",
        formatFunction: formatIsoToDateString,
    },
    latitude: {
        humanReadable: "Latitude",
        placeholder: "latitude",
        errorMessage: "Latitude should be between -90 and 90",
        formatFunction: formatLatitudeLongitude,
        isValidFn: (lat: number) => lat >= -90 && lat <= 90,
    },
    limitUnits: {
        humanReadable: "Limit unit",
    },
    location: {
        humanReadable: "Address",
        formatFunction: assembleBuildingAddress,
    },
    longitude: {
        humanReadable: "Longitude",
        placeholder: "longitude",
        errorMessage: "Longitude should be between -180 and 180",
        formatFunction: formatLatitudeLongitude,
        isValidFn: (lat: number) => lat >= -180 && lat <= 180,
    },
    marginalAbatementCost: {
        humanReadable: "Marginal abatement cost",
        unit: "$/tCO₂e saved",
        formatFunction: formatAbatementCost,
        tooltip: buildTooltipNode(
            "The negative NPV $ divided by the lifetime carbon emission savings of the proposed project.",
            "A negative value means the project offers a net benefit, where benefits exceed costs. Contrary, a positive value indicates a net cost, where project costs exceed benefits."
        ),
    },
    marginalAbatementCostIncremental: {
        humanReadable: "Marginal abatement cost incremental",
        unit: "$/tCO₂e saved",
        formatFunction: formatAbatementCost,
        tooltip: buildTooltipNode(
            "The negative incremental NPV $ divided by the lifetime carbon emission savings of the proposed project.",
            "A negative value means the project offers a net benefit, where benefits exceed costs. Contrary, a positive value indicates a net cost, where project costs exceed benefits."
        ),
    },
    marginalAbatementCostTotal: {
        humanReadable: "Marginal abatement cost total",
        unit: "$/tCO₂e saved",
        formatFunction: formatAbatementCost,
        tooltip: buildTooltipNode(
            "The negative total NPV $ divided by the lifetime carbon emission savings of the proposed project.",
            "A negative value means the project offers a net benefit, where benefits exceed costs. Contrary, a positive value indicates a net cost, where project costs exceed benefits."
        ),
    },
    measureCount: {
        humanReadable: "Measure count",
        formatFunction: (count: number) => count,
        unit: "",
    },
    measureCost: {
        humanReadable: "Low carbon upgrade cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    measureCoverage: {
        humanReadable: "Project coverage by number of buildings",
        formatFunction: formatMeasureCoverage,
    },
    name: {
        humanReadable: "Name",
    },
    netCashFlow: {
        humanReadable: "Net cash flow",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    netIncrementalCost: {
        humanReadable: "Net incremental cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip:
            "Net incremental cost of the project compared to the baseline cost of the like-for-like replacement including installation costs and including incentives.",
    },
    netPresentValue: {
        humanReadable: "NPV",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: buildTooltipNode(
            "The sum of all cash flows discounted back to their present value using the discount rate.",
            "If the NPV is positive, it suggests that the investment is expected to generate value and meet the required rate of return."
        ),
    },
    netPresentValueIncremental: {
        humanReadable: "NPV incremental",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: buildTooltipNode(
            "The sum of all cash flows discounted back to their present value using the discount rate.",
            "If the NPV is positive, it suggests that the investment is expected to generate value and meet the required rate of return."
        ),
    },
    netPresentValueIntensity: {
        humanReadable: "NPV intensity",
        unit: "$/ft²",
        getUnit: (v: number) => `${getMonetaryUnit(v, "netPresentValueIntensity")}/ft²`,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: buildTooltipNode(
            "NPV normalized by the building's gross floor area.",
            "Positive NPV suggests that the investment is expected to generate value. "
        ),
    },
    netPresentValueTotal: {
        humanReadable: "NPV total",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    netTotalCost: {
        humanReadable: "Net total cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "Total like for like and incremental cost minus incentives.",
    },
    numberOfBuildings: {
        humanReadable: "Number of buildings",
        unit: "Buildings",
    },
    numberOfIdentifiedProjects: {
        humanReadable: "Number of identified projects",
    },
    numberOfProjects: {
        humanReadable: "Number of projects",
    },
    landlordSplitBasis: {
        humanReadable: "Cost split basis",
        tooltip: "Are costs calculated with only landlord, only tenant, or both",
    },
    operatingCost: {
        humanReadable: "Total energy cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    planLifetimeCarbonCostsAvoided: {
        humanReadable: "Carbon policy cost savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    lifetimeCarbonCostsAvoided: {
        humanReadable: "Carbon policy cost savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    planLifetimeCarbonPolicyCosts: {
        humanReadable: "Carbon costs paid",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    postalZipCode: {
        humanReadable: "Postal code",
        placeholder: "Enter postal code",
    },
    propertyArchetype: {
        humanReadable: "Property type",
        formatFunction: formatBuildingArchetype,
        placeholder: "type",
    },
    propertyCount: {
        humanReadable: "Property count",
    },
    propertyName: {
        humanReadable: "Property name",
    },
    presentValue: {
        humanReadable: "Present Value",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    returnOnInvestment: {
        humanReadable: "ROI",
        unit: "%",
        formatFunction: (value: number) => formatNumberWithCommas(value, 0),
        tooltip: buildTooltipNode(
            "The lifetime utility cost savings and carbon policy cost savings divided by the total cost of the project.",
            "A positive ROI indicates that the project has generated utility savings.  A negative ROI indicates that the project has resulted in increased utility costs."
        ),
    },
    returnOnInvestmentIncremental: {
        humanReadable: "ROI incremental",
        unit: "%",
        formatFunction: (value: number) => formatNumberWithCommas(value, 0),
        tooltip: buildTooltipNode(
            "The lifetime utility cost savings and carbon policy cost savings divided by the incremental cost of the project.",
            "A positive ROI indicates that the project has generated utility savings.  A negative ROI indicates that the project has resulted in increased utility costs."
        ),
    },
    returnOnInvestmentTotal: {
        humanReadable: "ROI total",
        unit: "%",
        formatFunction: (value: number) => formatNumberWithCommas(value, 0),
        tooltip: buildTooltipNode(
            "The lifetime utility cost savings and carbon policy cost savings divided by the total cost of the project.",
            "A positive ROI indicates that the project has generated utility savings.  A negative ROI indicates that the project has resulted in increased utility costs."
        ),
    },
    roofArea: {
        humanReadable: "Area",
        unit: "ft²",
        formatFunction: formatBuildingAttribute,
    },
    roofInstallationYear: {
        humanReadable: "Installation year",
        errorMessage: `Must be between 1900 and ${CURRENT_YEAR}`,
        isValidFn: validateYear,
    },
    roofRValue: {
        humanReadable: "R value",
        formatFunction: formatBuildingAttribute,
    },
    simplePayback: {
        humanReadable: "Simple payback",
        unit: "yrs",
        formatFunction: formatSimplePayback,
        tooltip:
            "The number of years after which the money saved from implementing the project will cover the initial investment.",
    },
    simplePaybackIncremental: {
        humanReadable: "Simple payback incremental",
        unit: "yrs",
        formatFunction: formatSimplePayback,
        tooltip:
            "The number of years after which the money saved from implementing the project will cover the initial investment.",
    },
    simplePaybackTotal: {
        humanReadable: "Simple payback total",
        unit: "yrs",
        formatFunction: formatSimplePayback,
        tooltip:
            "The number of years after which the money saved from implementing the project will cover the initial investment.",
    },
    minPaybackPeriod: {
        humanReadable: "Minimum",
        unit: "yrs",
        formatFunction: formatSimplePaybackNotCapped,
        tooltip:
            "The number of years after which the money saved from implementing the project will cover the initial investment.",
    },
    maxPaybackPeriod: {
        humanReadable: "Maximum",
        unit: "yrs",
        formatFunction: formatSimplePaybackNotCapped,
        tooltip:
            "The number of years after which the money saved from implementing the project will cover the initial investment.",
    },
    totalIncentives: {
        humanReadable: "Incentives",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "Total value of incentives applied to project.",
    },
    recommendations: {
        humanReadable: "Recommendations",
        unit: "tCO₂e",
    },
    simplePaybackAverage: {
        humanReadable: "Simple payback average",
        unit: "yrs",
        formatFunction: formatSimplePayback,
        tooltip:
            "The average number of years after which the money saved from implementing the project will cover the initial investment.",
    },
    simplePaybackAverageNotCapped: {
        humanReadable: "Simple payback average",
        unit: "yrs",
        formatFunction: formatSimplePaybackNotCapped,
        tooltip:
            "The average number of years after which the money saved from implementing the project will cover the initial investment.",
    },
    simplePaybackYearsIncremental: {
        humanReadable: "Simple payback years incremental",
    },
    simplePaybackYearsTotal: {
        humanReadable: "Average simple payback",
        unit: "yrs",
        formatFunction: formatSimplePayback,
        tooltip:
            "The number of years after which the money saved from implementing the project will cover the initial investment.",
    },
    startDate: {
        humanReadable: "Start date",
        formatFunction: formatDate,
    },
    stateProvince: {
        humanReadable: "State/Province",
        placeholder: "Enter state/province",
    },
    streetAddress: {
        humanReadable: "Address",
        placeholder: "Enter address",
    },
    tags: {
        humanReadable: "Tags",
        formatFunction: formatTags,
    },
    totalAnnualCarbonEmissionsCurrent: {
        humanReadable: "Annual carbon emissions",
        unit: "tCO₂e",
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
        formatFunction: formatEmissions,
    },
    lifetimeEmissionsSavings: {
        humanReadable: "Lifetime Emissions Savings",
        unit: "tCO₂e",
        formatFunction: formatEmissions,
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
        tooltip:
            "The lifetime emission savings expected after project implementation, accounting for future grid projections.",
    },
    lifetimeTotalEmissionsSavings: {
        humanReadable: "Lifetime Emissions Savings",
        unit: "tCO₂e",
        formatFunction: formatEmissions,
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
        tooltip:
            "The lifetime emission savings expected after project implementation, accounting for future grid projections.",
    },
    totalAnnualOperatingCost: {
        humanReadable: "Total energy cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalCarbonEmissions: {
        humanReadable: "Total emissions produced",
        unit: "tCO₂e",
        formatFunction: formatEmissions,
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
    },
    averageEmissionsIntensity: {
        humanReadable: "Average emissions intensity",
        unit: "kg\xa0CO₂e/ft²",
        formatFunction: formatIntensity,
    },
    totalCarbonEmissionsElectricity: {
        humanReadable: "Electricity",
        unit: "tCO₂e",
        formatFunction: formatEmissions,
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
    },
    totalCarbonEmissionsNaturalGas: {
        humanReadable: "Natural gas",
        unit: "tCO₂e",
        formatFunction: formatEmissions,
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
    },
    totalCarbonEmissionSavings: {
        humanReadable: "Total emission savings",
        unit: "tCO₂e",
        formatFunction: formatEmissions,
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
    },
    totalCarbonEmissionSavingsElectricity: {
        humanReadable: "Electricity",
        unit: "tCO₂e",
        formatFunction: formatEmissions,
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
    },
    totalCarbonEmissionSavingsNaturalGas: {
        humanReadable: "Natural gas",
        unit: "tCO₂e",
        formatFunction: formatEmissions,
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
    },
    totalAnnualCarbonEmissionSavings: {
        humanReadable: "Average annual emission savings",
        unit: "tCO₂e",
        formatFunction: formatEmissions,
        getUnit: getEmissionsUnit,
        formatFromUnit: formatEmissionsFromUnit,
    },
    totalAnnualEnergyCostSavings: {
        humanReadable: "Average annual energy cost savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalAnnualEnergyConsumptionSavings: {
        humanReadable: "Average annual energy savings",
        unit: "kWh",
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
    },
    totalCarbonPolicyCostNoActions: {
        humanReadable: "Projected carbon policy cost | 2023 - 2050",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalCarbonPolicyCostSavings: {
        humanReadable: "Carbon policy cost savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalCostSavings: {
        humanReadable: "Net savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalEnergyCostSavings: {
        humanReadable: "Energy cost savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalEnergyCost: {
        humanReadable: "Total energy cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    landlordEnergyCost: {
        humanReadable: "Landlord energy cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    tenantEnergyCost: {
        humanReadable: "Tenant energy cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalEnergyConsumptionSavings: {
        humanReadable: "Total energy savings",
        unit: "kWh",
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
    },
    totalEnergyConsumptionSavingsElectricity: {
        humanReadable: "Electricity savings",
        unit: "kWh",
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
    },
    totalEnergyConsumptionSavingsNaturalGas: {
        humanReadable: "Natural gas savings",
        unit: "kWh",
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
    },
    totalEnergyConsumption: {
        humanReadable: "Total consumption",
        unit: "kWh",
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
    },
    averageEnergyIntensity: {
        humanReadable: "Average energy intensity",
        unit: "kWh/ft²",
        formatFunction: formatIntensity,
    },
    totalEnergyConsumptionElectricity: {
        humanReadable: "Electricity",
        unit: "kWh",
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
    },
    totalEnergyConsumptionNaturalGas: {
        humanReadable: "Natural gas",
        unit: "kWh",
        getUnit: getEnergyUnit,
        formatFunction: formatEnergy,
        formatFromUnit: formatEnergyFromUnit,
    },
    totalEnergyUseIntensity: {
        humanReadable: "Energy intensity",
        unit: "kWh/ft²",
        formatFunction: formatIntensity,
    },
    totalIncrementalCost: {
        humanReadable: "Incremental cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip:
            "Incremental cost of the project compared to the baseline cost of the like-for-like replacement including installation costs.",
    },
    totalLikeForLikeCost: {
        humanReadable: "Like for like cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalUtilityCostSavingsElectricity: {
        humanReadable: "Electricity savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalUtilityCostSavingsNaturalGas: {
        humanReadable: "Natural gas savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalMeasureCostIntensity: {
        humanReadable: "Cost per ft²",
        unit: "$/ft²",
        getUnit: (value: number) => `${getMonetaryUnit(value, "totalMeasureCostIntensity")}/ft²`,
        formatFunction: formatCostIntensity,
        formatFromUnit: (unit: string) => formatCostIntensityFromUnit(unit.replace("/ft²", "")),
        tooltip: "Project cost including installation costs normalized by floor area.",
    },
    revenue: {
        humanReadable: "Revenue",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalUtilityCostSavings: {
        humanReadable: "Utility savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    totalMeasureCost: {
        humanReadable: "Total cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "Project cost including installation costs.",
    },
    annualMeanLandlordUtilityCostSavings: {
        humanReadable: "Landlord utility cost savings",
        unit: "k $/yr",
        formatFromUnit: formatMonetaryValueFromUnit,
        getUnit: (value: number) => `${getMonetaryUnit(value)}${PER_YEAR}`,
        formatFunction: formatMonetaryValue,
        tooltip: "The portion of utility cost savings that accrues to the landlord, as an annual mean",
    },
    annualMeanTenantUtilityCostSavings: {
        humanReadable: "Tenant utility cost savings",
        unit: "k $/yr",
        getUnit: (value: number) => `${getMonetaryUnit(value)}${PER_YEAR}`,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
        tooltip: "The portion of utility cost savings that accrues to the tenant, as an annual mean",
    },
    type: {
        humanReadable: "Type",
    },
    utilityCost: {
        humanReadable: "Utility cost",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    utilityCostElectricity: {
        humanReadable: "Utility cost electricity",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    utilityCostNaturalGas: {
        humanReadable: "Utility cost natural gas",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    utilityCostIntensity: {
        humanReadable: "Utility cost intensity",
        unit: "$/ft²",
        formatFunction: formatCostIntensity,
    },
    utilityProvider: {
        humanReadable: "Supplier",
    },
    utilityRate: {
        humanReadable: "Rate",
        unit: "$/unit",
        formatFunction: formatUtilityRate,
    },
    utilityCostSavingsElectricity: {
        humanReadable: "Electricity savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    utilityCostSavingsNaturalGas: {
        humanReadable: "Natural gas savings",
        unit: "$",
        getUnit: getMonetaryUnit,
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    utilityType: {
        humanReadable: "Energy type",
        formatFunction: formatUtilityType,
    },
    wallRValue: {
        humanReadable: "Wall R Value",
        formatFunction: formatBuildingAttribute,
    },
    windowInstallationYear: {
        humanReadable: "Window installation year",
    },
    windowRValue: {
        humanReadable: "R value",
        formatFunction: formatBuildingAttribute,
    },
    windowSolarHeatGainCoefficient: {
        humanReadable: "Solar heat gain coefficient",
        formatFunction: formatBuildingAttribute,
    },
    windowToWallRatio: {
        humanReadable: "Window to wall ratio",
        formatFunction: formatRatio,
    },
    yearApplied: {
        humanReadable: "Implementation year",
        formatFunction: formatYearApplied,
        tooltip: "Suggested year for implementing this project.",
    },
    yearBuiltOriginal: {
        humanReadable: "Year built",
        placeholder: "Enter year",
        errorMessage: `Must be between 1900 and ${CURRENT_YEAR}`,
        isValidFn: validateYear,
    },
    year: {
        humanReadable: "Year",
    },
    years: {
        humanReadable: "Years",
        unit: "years",
    },
    yearStart: {
        humanReadable: "Start year",
    },
    yearEnd: {
        humanReadable: "End year",
    },
    ledInstalledRatio: {
        humanReadable: "LED installation",
        unit: "%",
        description:
            "The percentage of the total building floor area that is upgraded to LED (excluding exterior areas)",
        formatFunction: formatRatioAsPercentage,
        errorMessage: "Must be a valid percentage between 0 and 100",
        isValidFn: validatePercentage,
    },
    carbonFinesUnsaveable: {
        humanReadable: "Remaining carbon policy cost",
        unit: "$",
        getUnit: (v: number) => getMonetaryUnit(v, "carbonFinesUnsaveable"),
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    carbonFinesSaveable: {
        humanReadable: "Savings opportunity",
        unit: "$",
        getUnit: (v: number) => getMonetaryUnit(v, "carbonFinesSaveable"),
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    cumulativeCarbonFines: {
        humanReadable: "Cumulative carbon policy cost",
        unit: "$",
        getUnit: (v: number) => getMonetaryUnit(v, "cumulativeCarbonFines"),
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    cumulativeCarbonFineSavings: {
        humanReadable: "Cumulative carbon fine savings",
        unit: "$",
        getUnit: (v: number) => getMonetaryUnit(v, "cumulativeCarbonFineSavings"),
        formatFromUnit: formatMonetaryValueFromUnit,
        formatFunction: formatMonetaryValue,
    },
    carbonPolicy: {
        humanReadable: "Carbon Policy",
    },
    coolingMultiplier: {
        humanReadable: "Cooling Multiplier",
        recommendedRange: "0–10",
        isValidFn: (n: number) => n >= 0 && n <= 10,
        errorMessage: "Must be between 0 and 10",
    },
    heatingMultiplier: {
        humanReadable: "Heating Multiplier",
        recommendedRange: "0–10",
        isValidFn: (n: number) => n >= 0 && n <= 10,
        errorMessage: "Must be between 0 and 10",
    },
    lightingMultiplier: {
        humanReadable: "Lighting Multiplier",
        recommendedRange: "0–4",
        tooltip: "Using values outside the recommended range may result in inaccurate load modeling.",
        isValidFn: (n: number) => n >= 0 && n <= 4,
        errorMessage: "Must be between 0 and 4",
    },
    plugLoadMultiplier: {
        humanReadable: "Plug Load Multiplier",
        recommendedRange: "0–3",
        tooltip: "Using values outside the recommended range may result in inaccurate load modeling.",
        isValidFn: (n: number) => n >= 0 && n <= 3,
        errorMessage: "Must be between 0 and 3",
    },
    processMultiplier: {
        humanReadable: "Process Multiplier",
        recommendedRange: "0–5",
        isValidFn: (n: number) => n >= 0 && n <= 5,
        errorMessage: "Must be between 0 and 5",
    },
    domesticHotWaterMultiplier: {
        humanReadable: "DHW Multiplier",
        recommendedRange: "0–5",
        isValidFn: (n: number) => n >= 0 && n <= 5,
        errorMessage: "Must be between 0 and 5",
    },
    ventilationRateMultiplier: {
        humanReadable: "Ventilation Rate Multiplier",
        recommendedRange: "0–2",
        tooltip: "Using values outside the recommended range may result in inaccurate load modeling.",
        isValidFn: (n: number) => n >= 0 && n <= 2,
        errorMessage: "Must be between 0 and 2",
    },
    fansMultiplier: {
        humanReadable: "Fans Multiplier",
        recommendedRange: "0–20",
    },
    pumpsMultiplier: {
        humanReadable: "Pumps Multiplier",
        recommendedRange: "0–20",
    },
    defaultDiscountRate: {
        humanReadable: "Discount Rate Override",
        unit: "%",
        description: "",
        formatFunction: formatRatioAsPercentage,
        errorMessage: "Must be a valid percentage between 0 and 100",
        isValidFn: validatePercentage,
        recommendedRange: "5–15%",
    },
    defaultGridIntensityScheduleScenario: {
        humanReadable: "Grid Carbon Intensity Schedule",
    },
    defaultWeightEmissionSavings: {
        humanReadable: "Emission Savings Weight",
        unit: "%",
        description: "",
        formatFunction: formatRatioAsPercentage,
        errorMessage: "Must be a valid percentage between 0 and 100",
        isValidFn: validatePercentage,
        recommendedRange: "0–100%",
    },
    defaultWeightNetPresentValue: {
        humanReadable: "NPV Weight",
        unit: "%",
        description: "100% − Emission Savings Weight",
        formatFunction: formatRatioAsPercentage,
        recommendedRange: "100% − Emission Savings Weight",
    },
    defaultUtilityInflationRate: {
        humanReadable: "Inflation Rate Override",
        unit: "%",
        description: "",
        formatFunction: formatRatioAsPercentage,
        errorMessage: "Must be a valid percentage between 0 and 100",
        isValidFn: validatePercentage,
        recommendedRange: "0–5%",
    },
    defaultElectricityUtilityRate: {
        humanReadable: "Electricity Rate Default",
        formatFunction: formatMonetaryValue,
        isValidFn: validatePositiveNumber,
        errorMessage: "Must be a non-negative number",
    },
    defaultNaturalGasUtilityRate: {
        humanReadable: "Natural Gas Rate Default",
        formatFunction: formatMonetaryValue,
        isValidFn: validatePositiveNumber,
        errorMessage: "Must be a non-negative number",
    },
    defaultSteamUtilityRate: {
        humanReadable: "Steam Rate Default",
        formatFunction: formatMonetaryValue,
        isValidFn: validatePositiveNumber,
        errorMessage: "Must be a non-negative number",
    },
    assumedHeatingEnergyType: {
        humanReadable: "Assumed Heating Energy Type",
    },
    assumedCoolingEnergyType: {
        humanReadable: "Assumed Cooling Energy Type",
    },
    assumedDhwEnergyType: {
        humanReadable: "Assumed DHW Energy Type",
    },
    defaultLandlordShareCost: {
        humanReadable: "Cost Share",
        unit: "%",
        description: "",
        formatFunction: formatRatioAsPercentage,
        errorMessage: "Must be a valid percentage between 0 and 100",
        isValidFn: validatePercentage,
    },
    defaultLandlordShareElectricity: {
        humanReadable: "Electricity Share",
        unit: "%",
        description: "",
        formatFunction: formatRatioAsPercentage,
        errorMessage: "Must be a valid percentage between 0 and 100",
        isValidFn: validatePercentage,
    },
    defaultLandlordShareNaturalGas: {
        humanReadable: "Natural Gas Share",
        unit: "%",
        description: "",
        formatFunction: formatRatioAsPercentage,
        errorMessage: "Must be a valid percentage between 0 and 100",
        isValidFn: validatePercentage,
    },
    defaultLandlordShareSteam: {
        humanReadable: "Steam Share",
        unit: "%",
        description: "",
        formatFunction: formatRatioAsPercentage,
        errorMessage: "Must be a valid percentage between 0 and 100",
        isValidFn: validatePercentage,
    },
    assumedGrossAssetValue: {
        humanReadable: "Assumed Gross Asset Value",
        formatFunction: formatMonetaryValue,
        isValidFn: validatePositiveNumber,
        errorMessage: "Must be a non-negative number",
    },
    customerBuildingIdentifier: {
        humanReadable: "Customer Building Identifier",
    },
    notes: {
        humanReadable: "Notes",
    },
    leasingStructure: {
        humanReadable: "Leasing Structure",
    },
    assumedExitCapRate: {
        humanReadable: "Assumed Exit Cap Rate",
        unit: "%",
        description: "",
        formatFunction: formatRatioAsPercentage,
        errorMessage: "Must be a valid percentage between 0 and 100",
        isValidFn: validatePercentage,
        recommendedRange: "5–15%",
    },
    assumedExitYear: {
        humanReadable: "Assumed Exit Year",
        isValidFn: validateExitYear,
        errorMessage: `Must be a year between ${CURRENT_YEAR} and 2100`,
    },
};

interface DisplayInfoReturnValue extends DisplayInfo {
    formatFunction: Function;
    unit: string;
}

export const getDisplayInfo = (key: string, value?: number | number[]): DisplayInfoReturnValue => {
    if (Array.isArray(value)) {
        const nonZeroAbsValues = value.map((d) => Math.abs(d)).filter((v) => v > 0.01); // this is a workaround because the min net cash flow is 0.01

        const min = nonZeroAbsValues.length > 0 ? Math.min(...nonZeroAbsValues) : 0;

        return getDisplayInfo(key, min);
    }
    const {
        humanReadable,
        unit: defaultUnit,
        formatFunction,
        placeholder,
        isValidFn,
        getUnit,
        formatFromUnit,
        errorMessage,
        tooltip,
        recommendedRange,
    } = getDisplayInfoForKey(key);

    // console.log("key", key)--
    const unit = value && getUnit ? getUnit(value) : defaultUnit;

    if (formatFromUnit && getUnit) {
        const format = formatFromUnit ? formatFromUnit(unit) : formatFunction;
        return {
            formatFunction: format,
            humanReadable,
            unit,
            formatFromUnit,
            placeholder,
            errorMessage,
            isValidFn,
            tooltip,
            recommendedRange,
        };
    }

    return {
        unit,
        formatFunction,
        humanReadable,
        placeholder,
        isValidFn,
        errorMessage,
        tooltip,
        recommendedRange,
    };
};

export const displayValueWithUnit = (
    value: string | number,
    unit: string,
    formatFunction: (value: string | number) => string
) => {
    if (unit.includes("$")) {
        const remainingUnit = unit.replace("$", "");
        if (Number(value) < 0) return `-$${formatFunction(Math.abs(Number(value)))}${remainingUnit}`;
        return `$${formatFunction(value)}${remainingUnit}`;
    }
    return `${formatFunction(value)}${unit === "%" ? "" : " "}${unit}`;
};

const getDisplayInfoForKey = (key: string) => {
    if (!(key in semanticTermToDisplayInfo)) {
        /* [2025-09-19 TRC] TODO
            We shouldn't have to explicitly define every single column we might ever want to
            display.  If the key isn't in semanticTermToDisplayInfo then, instead of throwing
            this error, we should have a fallback, generic formatter that just humanizes
            the attribute name and casts it to a string with no units and we all move on with our
            lives.  We should only have to define something in semanticTermToDisplayInfo if
            it actually needs special formatting beyond just regurgitating a string.
        */
        throw new Error(`Please define display info in the semanticTermToDisplayInfo dictionary for ${key}`);
    }
    const {
        humanReadable,
        unit,
        formatFunction,
        placeholder,
        isValidFn,
        getUnit,
        formatFromUnit,
        errorMessage,
        tooltip,
        recommendedRange,
    } = semanticTermToDisplayInfo[key];

    // console.log(key)

    if (!humanReadable)
        throw new Error(`Please define display info in the semanticTermToDisplayInfo dictionary for ${key}`);
    return {
        humanReadable,
        unit: unit || "",
        formatFunction: formatFunction || ((v: any) => v),
        placeholder,
        getUnit,
        formatFromUnit,
        isValidFn: isValidFn || ((v: any) => true),
        errorMessage,
        tooltip,
        recommendedRange,
    };
};

export const endUseValueToDisplayLabelMap: { [key: string]: string } = {
    electricity: "Electricity",
    renewable: "Renewable",
    domestic_hot_water: "Domestic hot water",
    lighting: "Lighting",
    natural_gas: "Natural gas",
    fans: "Fans",
    pumps: "Pumps",
    outdoor_air_cooling: "Outdoor air cooling",
    outdoor_air_heating: "Outdoor air heating",
    plug_load: "Plug load",
    skin_cooling: "Skin cooling",
    skin_heating: "Skin heating",
    process: "Process",
    cooling: "Cooling",
    heating: "Heating",
};

export const endUseKeyToDisplayLabelMap: { [key: string]: string } = {
    electricity: "Electricity",
    renewable: "Renewable",
    domesticHotWater: "Domestic hot water",
    lighting: "Lighting",
    naturalGas: "Natural gas",
    fans: "Fans",
    pumps: "Pumps",
    outdoorAirCooling: "Outdoor air cooling",
    outdoorAirHeating: "Outdoor air heating",
    plugLoad: "Plug load",
    skinCooling: "Skin cooling",
    skinHeating: "Skin heating",
    process: "Process",
    cooling: "Cooling",
    heating: "Heating",
};

export const getTextColorByValue = (value: number, reverse?: boolean) => {
    const isNegative = reverse ? value > 0 : value < 0;
    const isPositive = reverse ? value < 0 : value > 0;
    const isNeutral = Math.round(value) === 0;

    let color;
    if (isNegative) color = "var(--audette-content-negative)";
    if (isPositive) color = "var(--audette-content-positive)";
    if (isNeutral) color = "var(--audette-gray-500)";

    return color;
};

export const formatDuration = (duration: number): string => {
    if (duration < 60) {
        return `${duration.toFixed(1)} seconds`;
    }
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);

    if (seconds === 0) {
        return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    }
    if (minutes === 0) {
        return `${seconds.toFixed(1)} seconds`;
    }
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ${seconds.toFixed(0)} ${seconds === 1 ? "second" : "seconds"}`;
};

export function roundTo(num: number, precision: number): number {
    const factor: number = 10 ** precision;
    return Math.round(num * factor) / factor;
}
